/**
 * Origin handoff from Intelligence Insights modules into Ask Lens.
 *
 * When opened from a module, Ask Lens uses only that module's stored analysis
 * and evidence — not the wider project corpus.
 */

export const INSIGHTS_ASK_LENS_SOURCE = 'intelligence_insights';

export const INSIGHTS_MODULE_TITLES = {
    highlights: 'Highlights',
    management_summary: 'Management Summary',
    key_findings: 'Key Findings',
    swot_analysis: 'SWOT Analysis',
    investment_opinion: 'Investment Opinion',
    remediation_investment: 'Remediation & Investment',
    risks_signals: 'Risks & Signals',
    evidence_follow_ups: 'Evidence & Follow-Ups',
};

const MODULE_CONTEXT_STORAGE_PREFIX = 'lens.insightsAskModuleContext.';
const EVIDENCE_ID_KEYS = [
    'evidence_reference_ids',
    'related_finding_ids',
    'related_risk_ids',
    'related_theme_ids',
    'related_evidence_matter_ids',
];

const moduleContextMemory = new Map();

function titleCaseToken(value) {
    if (!value) return '';
    return String(value)
        .replace(/_/g, ' ')
        .replace(/\b\w/g, ch => ch.toUpperCase());
}

function firstNonEmpty(...values) {
    for (const value of values) {
        if (value == null) continue;
        const text = String(value).trim();
        if (text) return text;
    }
    return null;
}

export function insightsModuleTitle(moduleKey, fallbackTitle) {
    if (fallbackTitle) return String(fallbackTitle);
    if (moduleKey && INSIGHTS_MODULE_TITLES[moduleKey]) {
        return INSIGHTS_MODULE_TITLES[moduleKey];
    }
    return titleCaseToken(moduleKey) || 'Intelligence Insights';
}

export function formatInsightsVersionLabel(version) {
    if (version == null || version === '') return null;
    const text = String(version).trim();
    if (!text) return null;
    if (/^version\b/i.test(text)) return text;
    return `Version ${text}`;
}

export function isInsightsModuleAskOrigin(origin) {
    if (!origin || typeof origin !== 'object') return false;
    const source = origin.source || origin.insights_origin?.source;
    const moduleKey =
        origin.moduleKey ||
        origin.module_key ||
        origin.originating_module ||
        origin.insights_origin?.originating_module;
    return source === INSIGHTS_ASK_LENS_SOURCE || Boolean(moduleKey);
}

export function insightsModuleAskTooltip(origin) {
    const title = origin?.moduleTitle || insightsModuleTitle(origin?.moduleKey) || 'this module';
    const version = origin?.analysisVersion ?? '—';
    return (
        `This chat uses only the stored ${title} analysis (Version ${version}). ` +
        'It does not search the wider project corpus or regenerate the analysis.'
    );
}

export function formatInsightsOriginIndicator(origin) {
    if (!origin) return '';
    const versionLabel =
        origin.versionLabel || formatInsightsVersionLabel(origin.analysisVersion);
    const parts = [
        'Intelligence Insights',
        origin.moduleTitle || insightsModuleTitle(origin.moduleKey),
        versionLabel,
    ];
    if (origin.scopeAreaLabel) parts.push(origin.scopeAreaLabel);
    return parts.filter(Boolean).join(' | ');
}

function collectEvidenceIds(value, out = new Set()) {
    if (!value || typeof value !== 'object') return out;
    if (Array.isArray(value)) {
        value.forEach(item => collectEvidenceIds(item, out));
        return out;
    }
    for (const key of EVIDENCE_ID_KEYS) {
        const ids = value[key];
        if (Array.isArray(ids)) {
            ids.forEach(id => {
                if (id != null && String(id).trim()) out.add(String(id));
            });
        }
    }
    Object.keys(value).forEach(key => {
        if (EVIDENCE_ID_KEYS.includes(key)) return;
        const nested = value[key];
        if (nested && typeof nested === 'object') collectEvidenceIds(nested, out);
    });
    return out;
}

export function buildInsightsModuleAskContext(
    payload,
    { moduleKey, moduleTitle, scopeArea = null, scopeAreaLabel = null } = {},
) {
    if (!payload || typeof payload !== 'object') return null;
    const analysis = payload.analysis || {};
    const resolvedKey = moduleKey || payload.screen_key || payload.module_key || null;
    const generation = payload.context && typeof payload.context === 'object' ? payload.context : {};
    return {
        module_key: resolvedKey,
        module_title: insightsModuleTitle(resolvedKey, moduleTitle),
        analysis: {
            analysis_id: analysis.analysis_id || analysis.analysisId || null,
            version: analysis.version ?? analysis.analysis_version ?? null,
            source_snapshot_id: analysis.source_snapshot_id || analysis.sourceSnapshotId || null,
            status: analysis.status || null,
            generated_at: analysis.generated_at || null,
        },
        generation_context: {
            snapshot_type: generation.type || generation.context_type || null,
            snapshot_label: generation.label || generation.context_label || null,
        },
        trust: payload.trust || {},
        warnings: payload.warnings || [],
        report: payload.data && typeof payload.data === 'object' ? payload.data : {},
        evidence_reference_ids: [...collectEvidenceIds(payload)],
        focus_scope_area: scopeArea || null,
        focus_scope_area_label: scopeAreaLabel || null,
    };
}

export function storeInsightsModuleAskContext(origin, moduleContext) {
    if (!origin) return origin;
    const key =
        origin.moduleContextKey ||
        `${origin.analysisId || 'na'}:${origin.moduleKey || 'module'}:${origin.openedAt || Date.now()}`;
    if (moduleContext) {
        moduleContextMemory.set(key, moduleContext);
        try {
            if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem(
                    `${MODULE_CONTEXT_STORAGE_PREFIX}${key}`,
                    JSON.stringify(moduleContext),
                );
            }
        } catch {
            // Quota — in-memory copy still covers this tab session.
        }
    }
    return { ...origin, moduleContextKey: key, moduleContext: moduleContext || origin.moduleContext || null };
}

export function readInsightsModuleAskContext(origin) {
    if (!origin) return null;
    if (origin.moduleContext && typeof origin.moduleContext === 'object') {
        return origin.moduleContext;
    }
    const key = origin.moduleContextKey;
    if (!key) return null;
    if (moduleContextMemory.has(key)) return moduleContextMemory.get(key);
    try {
        if (typeof sessionStorage === 'undefined') return null;
        const raw = sessionStorage.getItem(`${MODULE_CONTEXT_STORAGE_PREFIX}${key}`);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        moduleContextMemory.set(key, parsed);
        return parsed;
    } catch {
        return null;
    }
}

export function buildInsightsAskLensOrigin({
    moduleKey,
    moduleTitle,
    analysis = {},
    context = {},
    analysisContext = {},
    scopeArea = null,
    scopeAreaLabel = null,
    modulePayload = null,
} = {}) {
    const version = analysis.version ?? analysis.analysisVersion ?? analysis.analysis_version ?? null;
    const contextLabel =
        firstNonEmpty(analysisContext.contextLabel, context.label, context.context_label) ||
        'Entire Project';
    const contextType =
        firstNonEmpty(analysisContext.contextType, context.type, context.context_type) ||
        'entire_project';
    const origin = {
        source: INSIGHTS_ASK_LENS_SOURCE,
        moduleKey: moduleKey || null,
        moduleTitle: insightsModuleTitle(moduleKey, moduleTitle),
        analysisId: analysis.analysis_id || analysis.analysisId || null,
        analysisVersion: version,
        versionLabel: formatInsightsVersionLabel(version),
        sourceSnapshotId: analysis.source_snapshot_id || analysis.sourceSnapshotId || null,
        contextType,
        contextLabel,
        workstreamIds: Array.isArray(analysisContext.workstreamIds)
            ? analysisContext.workstreamIds
            : undefined,
        scopeArea: scopeArea || null,
        scopeAreaLabel: scopeAreaLabel || null,
        interrogateExistingAnalysis: true,
    };
    origin.indicatorLabel = formatInsightsOriginIndicator(origin);
    const moduleContext = buildInsightsModuleAskContext(modulePayload, {
        moduleKey: origin.moduleKey,
        moduleTitle: origin.moduleTitle,
        scopeArea,
        scopeAreaLabel,
    });
    return storeInsightsModuleAskContext(origin, moduleContext);
}

export function normalizeInsightsAskLensOrigin(raw) {
    if (!raw || typeof raw !== 'object') return null;
    const nested = raw.insights_origin && typeof raw.insights_origin === 'object' ? raw.insights_origin : raw;
    const moduleKey = nested.moduleKey || nested.module_key || nested.originating_module || null;
    const analysisVersion =
        nested.analysisVersion ?? nested.analysis_version ?? nested.version ?? null;
    const origin = {
        source: nested.source || INSIGHTS_ASK_LENS_SOURCE,
        moduleKey,
        moduleTitle: insightsModuleTitle(
            moduleKey,
            nested.moduleTitle || nested.module_title || nested.originating_module_title,
        ),
        analysisId: nested.analysisId || nested.analysis_id || null,
        analysisVersion,
        versionLabel:
            nested.versionLabel || nested.version_label || formatInsightsVersionLabel(analysisVersion),
        sourceSnapshotId: nested.sourceSnapshotId || nested.source_snapshot_id || null,
        contextType: nested.contextType || nested.context_type || nested.generation_context_type || 'entire_project',
        contextLabel: nested.contextLabel || nested.context_label || nested.generation_context_label || 'Entire Project',
        workstreamIds: Array.isArray(nested.workstreamIds)
            ? nested.workstreamIds
            : Array.isArray(nested.generation_workstream_ids)
              ? nested.generation_workstream_ids
              : undefined,
        scopeArea: nested.scopeArea || nested.scope_area || null,
        scopeAreaLabel: nested.scopeAreaLabel || nested.scope_area_label || null,
        conversationId: nested.conversationId || nested.conversation_id || null,
        interrogateExistingAnalysis: nested.interrogateExistingAnalysis !== false,
        openedAt: nested.openedAt || nested.opened_at || null,
        moduleContextKey: nested.moduleContextKey || nested.module_context_key || null,
        moduleContext: nested.moduleContext || nested.module_context || null,
    };
    origin.indicatorLabel = nested.indicatorLabel || formatInsightsOriginIndicator(origin);
    return origin;
}

export function serializeInsightsOriginForAsk(origin) {
    const normalized = normalizeInsightsAskLensOrigin(origin);
    if (!normalized) return null;
    const moduleContext = readInsightsModuleAskContext(normalized);
    const payload = {
        source: normalized.source,
        originating_module: normalized.moduleKey,
        originating_module_title: normalized.moduleTitle,
        analysis_id: normalized.analysisId,
        analysis_version: normalized.analysisVersion,
        source_snapshot_id: normalized.sourceSnapshotId,
        generation_context_type: normalized.contextType,
        generation_context_label: normalized.contextLabel,
        interrogate_existing_analysis: true,
        do_not_regenerate: true,
        prefer_canonical_module_as_context: true,
        module_only_context: true,
        retrieval_mode: 'insights_module',
        origin_hint: normalized.indicatorLabel,
    };
    if (Array.isArray(normalized.workstreamIds) && normalized.workstreamIds.length) {
        payload.generation_workstream_ids = normalized.workstreamIds;
    }
    if (normalized.scopeArea) {
        payload.scope_area = normalized.scopeArea;
        payload.scope_area_label = normalized.scopeAreaLabel || null;
    }
    if (moduleContext) {
        payload.module_context = moduleContext;
    }
    return payload;
}

export function originFromConversation(conversation) {
    if (!conversation || typeof conversation !== 'object') return null;
    const scope = conversation.scope_json || conversation.scopeJson || conversation.meta || {};
    const raw =
        conversation.insights_origin ||
        conversation.insightsOrigin ||
        scope.insights_origin ||
        scope.insightsOrigin ||
        null;
    const origin = normalizeInsightsAskLensOrigin(raw);
    if (!origin) return null;
    return {
        ...origin,
        conversationId: conversation.id || origin.conversationId || null,
    };
}
